/**
 * SanPlumb — Generador de SystemData para el visor
 *
 * Helper compartido entre el visor (render) y los paneles (BOM/info) para
 * que ambos trabajen sobre el MISMO SystemData. Compone:
 *
 *   counts + tipología (§XI)  →  generateBathSystem  →  room + sanitaries
 *                              →  applyStrategy      →  pipes + accesorios
 *                              →  SystemData completo
 *
 * Antes (v1.10 y previos) usaba un DEMO_SYSTEM fijo, lo que hacía que el
 * visor mostrara siempre el mismo baño (inodoro + lavatorio + ducha)
 * independientemente de lo que el usuario declarara en Hunter. Desde v1.11
 * el visor responde al `counts` real y a la tipología §XI elegida o detectada.
 */

import type { RoutingStrategy, SystemData } from '../../types';
import type { CountsState, RoomDims, UsoMode } from '../../types/ui';
import type { TipologiaCode } from '../../constants/tipologias';
import { generateBathSystem } from '../generateBath';
import { applyStrategy } from './sceneBuilder';

/**
 * Construye un SystemData completo a partir del `counts` declarado, la
 * tipología §XI seleccionada (o detectada) y las dimensiones reales del
 * recinto declaradas por el usuario en la ingesta.
 *
 * @param routing     Estrategia de routing — en v1.10+ siempre 'muro_dual'.
 * @param counts      Cantidades de aparatos por id (del Hunter).
 * @param tipologia   Código §XI (A1, B1, SH, ACC…). Si no hay roomDims
 *                    se usan sus dimensiones por defecto.
 * @param mode        'priv' (residencial) o 'pub' — afecta UG.
 * @param roomDims    Dimensiones reales del baño declaradas por el usuario.
 *                    Cuando están presentes, override la tabla §XI: los
 *                    muros virtuales se construyen contra estos valores.
 */
export function systemForRouting(
  routing: RoutingStrategy,
  counts: CountsState,
  tipologia: TipologiaCode,
  mode: UsoMode = 'priv',
  roomDims?: RoomDims,
): SystemData {
  const base = generateBathSystem(counts, tipologia, mode, roomDims);

  // Sin aparatos no hay routing que generar — devolvemos el sistema vacío
  // (solo room) para que el visor muestre el recinto pelado.
  if (base.sanitaries.length === 0) {
    return { ...base, routingStrategy: routing };
  }

  const gen = applyStrategy(routing, base.sanitaries, base.room);
  return {
    ...base,
    pipes: gen.pipes,
    pipes_ac: gen.pipes_ac,
    accessories: gen.accessories,
    accessories_ac: gen.accessories_ac,
    routingStrategy: routing,
  };
}
